import { Transform } from 'class-transformer';
import {
  IsBoolean,
  IsDateString,
  IsIn,
  IsNotEmpty,
  IsOptional,
  IsString,
  Length,
  MaxLength,
  ValidateIf,
} from 'class-validator';

import { OrderType } from '../orders/order-type.enum';
import { OriginApp } from '../origin-app.enum';
import { extractPlainDate } from '../utils';
import { CreateDraftLineDto } from './create-draft-line.dto';

export class CreateDraftDto {
  /**
   * the requested date for the order delivery
   * @example '2022-07-19T12:00:00.000Z'
   */
  @IsDateString()
  @IsNotEmpty()
  @Transform(({ value }) => extractPlainDate(value))
  public requestedDate: string;

  /**
   * The order type
   * @example 'Re-Order'
   */
  @IsNotEmpty()
  @IsIn(Object.values(OrderType))
  public type: OrderType;

  /**
   * The lines to be ordered
   */
  @IsNotEmpty()
  public lines: CreateDraftLineDto[];

  /**
   * true if sales rep is creating the order, for the sake of contract validating the contactId
   */
  @IsNotEmpty()
  public isSalesRep: boolean;

  /**
   * In case a sales-rep is creating an order for his account,
   * then a salesforce contact id is required for notification puroposes
   */
  @ValidateIf((createDraft: CreateDraftDto) => createDraft.isSalesRep)
  @IsNotEmpty()
  public contactId?: string;

  /**
   * The app from where the draft creation is being triggered
   */
  @ValidateIf((createDraft: CreateDraftDto) => !!createDraft.origin)
  @IsIn(Object.values(OriginApp))
  public origin?: OriginApp;

  /**
   * Order number corresponding to the order from which this draft order was cloned/created.
   * Must be a sales order number or a draft order number
   * @example SO01B0041950U
   */
  @IsOptional()
  @Length(12, 13)
  public sourceOrderNumber?: string;

  /**
   * When creating multiple pre-orders, we should be able to group them using a groupId
   * @example '9a48e37a-e9f1-43e8-a1d4-a933ef1bbb8e'
   */
  @IsOptional()
  @IsString()
  @IsNotEmpty()
  @MaxLength(50)
  public groupId?: string;

  /**
   * Customer PO number / reference, pre-filled from Excel template upload
   * @example 'PO-2024-001'
   */
  @IsOptional()
  @IsString()
  @MaxLength(20)
  public customerPoNumber?: string;

  /**
   * Marks this draft as created via Excel upload
   */
  @IsOptional()
  @IsBoolean()
  public isCreatedViaExcelUpload?: boolean;
}
